import React, { useState, useRef, useEffect } from 'react';
import styles from './CustomAudioPlayer.module.css';
import { Download, Play, Pause } from 'lucide-react';
import CustomTooltip from '../Tooltip/Tooltip';

const CustomAudioPlayer = ({ audioBlob }) => {
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [audioUrl, setAudioUrl] = useState(null);
    const audioRef = useRef(null);
    const progressRef = useRef(null);

    useEffect(() => {
        // Cleanup previous URL
        if (audioUrl) {
            URL.revokeObjectURL(audioUrl);
            setAudioUrl(null);
        }

        if (!audioBlob || audioBlob.size === 0) {
            setIsLoading(true);
            return;
        }

        try {
            // Always create a new blob with explicit type
            const validBlob = new Blob([audioBlob], {
                type: 'audio/webm;codecs=opus'
            });

            if (!validBlob.size) {
                throw new Error('Failed to create valid audio blob');
            }

            const url = URL.createObjectURL(validBlob);

            if (audioRef.current) {
                audioRef.current.src = url;
                audioRef.current.load();

                const loadCheck = () => {
                    audioRef.current.removeEventListener('loadedmetadata', loadCheck);
                };
                audioRef.current.addEventListener('loadedmetadata', loadCheck);
            }

            setAudioUrl(url);
            setIsLoading(false);

        } catch (error) {
            console.error('Error setting up audio player:', {
                error: error.message,
                stack: error.stack,
                blobDetails: {
                    size: audioBlob?.size,
                    type: audioBlob?.type
                }
            });
            setIsLoading(false);
        }

        return () => {
            if (audioUrl) {
                URL.revokeObjectURL(audioUrl);
            }
        };
        // eslint-disable-next-line
    }, [audioBlob]);

    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return;

        const handleTimeUpdate = () => {
            setCurrentTime(audio.currentTime);
        };

        const handleLoadedMetadata = () => {
            setDuration(audio.duration);
            setIsLoading(false);
        };

        const handleEnded = () => {
            setIsPlaying(false);
            setCurrentTime(0);
            audio.currentTime = 0;
        };

        const handleError = (error) => {
            console.error('Audio element error:', {
                error: error.target.error,
                errorCode: error.target.error?.code,
                errorMessage: error.target.error?.message,
                currentSrc: audio.currentSrc,
                readyState: audio.readyState,
                networkState: audio.networkState
            });
            setIsLoading(false);
        };

        // Add event listeners
        audio.addEventListener('timeupdate', handleTimeUpdate);
        audio.addEventListener('loadedmetadata', handleLoadedMetadata);
        audio.addEventListener('ended', handleEnded);
        audio.addEventListener('error', handleError);

        // Cleanup listeners
        return () => {
            audio.removeEventListener('timeupdate', handleTimeUpdate);
            audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
            audio.removeEventListener('ended', handleEnded);
            audio.removeEventListener('error', handleError);
        };
    }, []);

    const togglePlay = async () => {
        if (!audioRef.current || isLoading) return;

        try {
            if (isPlaying) {
                await audioRef.current.pause();
            } else {
                await audioRef.current.play();
            }
            setIsPlaying(!isPlaying);
        } catch (error) {
            console.error('Error toggling play:', error);
        }
    };

    const handleProgressClick = (e) => {
        if (!audioRef.current || !duration) return;

        const bounds = progressRef.current.getBoundingClientRect();
        const x = e.clientX - bounds.left;
        const percentage = x / bounds.width;
        const newTime = percentage * duration;

        audioRef.current.currentTime = newTime;
        setCurrentTime(newTime);
    };

    const formatTime = (time) => {
        if (!isFinite(time) || time === 0) return '0:00';
        const minutes = Math.floor(time / 60);
        const seconds = Math.floor(time % 60);
        return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    };

    const handleDownload = () => {
        if (!audioBlob) return;

        const url = URL.createObjectURL(new Blob([audioBlob], { type: 'audio/webm;codecs=opus' }));
        const a = document.createElement('a');
        a.href = url;
        a.download = `recording-${Date.now()}.webm`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    return (
        <div className={styles.playerContainer}>
            <audio
                ref={audioRef}
                preload="metadata"
                onLoadStart={() => setIsLoading(true)}
                onCanPlay={() => setIsLoading(false)}
            />

            <div className={styles.controls}>
                <button
                    className={`${styles.controlButton} ${styles.playButton} ${isLoading ? styles.disabled : ''}`}
                    onClick={togglePlay}
                    disabled={isLoading}
                    aria-label={isPlaying ? 'Pause' : 'Play'}
                >
                    <span className={styles.buttonIcon}>
                        {isPlaying ? <Pause size={14} /> : <Play size={14} />}
                    </span>
                </button>

                <div className={styles.progressContainer}>
                    <div
                        className={styles.progressBar}
                        ref={progressRef}
                        onClick={handleProgressClick}
                    >
                        <div
                            className={styles.progress}
                            style={{
                                width: `${duration ? (currentTime / duration) * 100 : 0}%`
                            }}
                        />
                    </div>
                    <div className={styles.timeDisplay}>
                        <span>{formatTime(currentTime)}</span>
                        <span>{formatTime(duration)}</span>
                    </div>
                </div>

                <CustomTooltip text="Audio files are temporary and will not be saved in the application. Download to keep a permanent copy on your device." isDownloadButton={true}>
                    <button
                        className={`${styles.controlButton} ${styles.downloadButton} ${isLoading ? styles.disabled : ''}`}
                        onClick={handleDownload}
                        disabled={isLoading}
                        aria-label="Download recording"
                    >
                        <span className={styles.buttonIcon}><Download size={14} /></span>
                    </button>
                </CustomTooltip>
            </div>
        </div>
    );
};

export default CustomAudioPlayer;